import { OrderStatus, useGetAllClientOrdersQuery } from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { Box, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { SearchInput } from '../../components/ui/search-input';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { StatusBadge } from '../../components/ui/status-badge';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { CurrencyText } from '../../components/CurrencyText';
import { formatDate } from '../../lib/utils';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useCurrencyStore } from '../../stores/currency';
import { ProductImage, primaryProductImage } from '../../components/ui/product-image';

/* ── Status config ── */

const STATUS_CONFIG: Record<
	string,
	{ label: string; tone: 'success' | 'warn' | 'info' | 'neutral' | 'danger' }
> = {
	[OrderStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderStatus.InProduction]: { label: 'In Production', tone: 'info' },
	[OrderStatus.Completed]: { label: 'Completed', tone: 'success' },
	[OrderStatus.Cancelled]: { label: 'Cancelled', tone: 'danger' },
};

function OrderStatusBadge({ status }: { status: string }) {
	const config = STATUS_CONFIG[status] ?? { label: status, tone: 'neutral' as const };
	return <StatusBadge active activeLabel={config.label} activeTone={config.tone} />;
}

/* ── Skeletons ── */

function TableSkeleton() {
	return (
		<Card className="overflow-hidden border-line-1 bg-surface">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-4 px-4 py-3.5"
					style={{ borderBottom: i < 5 ? '0.5px solid var(--line-1)' : 'none' }}
				>
					<div className="h-4 w-24 animate-pulse rounded bg-surface-fill" />
					<div className="h-4 w-32 animate-pulse rounded bg-surface-fill" />
					<div className="h-5 w-16 animate-pulse rounded bg-surface-fill" />
					<div className="ml-auto h-4 w-20 animate-pulse rounded bg-surface-fill" />
				</div>
			))}
		</Card>
	);
}

/**
 * One order row, for sizing scroll-mode batches: TableCell's 10px vertical padding around the
 * name (13px × 1.5) over the order number (11px × 1.5), plus the 1px border.
 */
function orderRows(): RowEstimate {
	return { rowHeight: 57 };
}

/* ════════════════════════ ORDERS PAGE ════════════════════════ */

export function OrdersPage() {
	const navigate = useNavigate();
	const { selectedCurrency } = useCurrencyStore();
	const {
		search,
		setSearch,
		debouncedSearch,
		setPage,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		mode,
		setMode,
		request,
		loadMore,
		limit,
	} = useListParams({ defaultSort: 'createdAt', defaultDir: 'desc', scrollRows: orderRows });
	const [tab, setTab] = useQueryParam('tab', 'all');

	const statusFilter = tab === 'all' ? undefined : [tab as OrderStatus];

	const { data, loading } = useGetAllClientOrdersQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				statuses: statusFilter,
			},
			inCurrency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllClientOrders,
		loading
	);
	const orders = list.items;

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* Header */}
			<div className="mb-6">
				<h1 className="mb-1 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
					Orders
				</h1>
				<p className="text-base text-text-3">Track and manage your orders.</p>
			</div>

			{/* Tabs + search */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All' },
						{ value: OrderStatus.Pending, label: 'Pending' },
						{ value: OrderStatus.InProduction, label: 'In Production' },
						{ value: OrderStatus.Completed, label: 'Completed' },
						{ value: OrderStatus.Cancelled, label: 'Cancelled' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search orders…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* Content */}
			{list.loading ? (
				<TableSkeleton />
			) : orders.length === 0 ? (
				<Card className="border-line-1 bg-surface py-16">
					<div className="flex flex-col items-center gap-3 text-center">
						<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
							<Truck size={20} />
						</div>
						<div className="text-base text-text-2">No orders found</div>
						<div className="text-sm text-text-4">
							{search
								? 'Try a different search term.'
								: 'Orders will appear here once created.'}
						</div>
					</div>
				</Card>
			) : (
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Order
									</SortableTableHead>
									<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Items
									</TableHead>
									<TableHead className="w-[100px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Status
									</TableHead>
									<SortableTableHead
										sortKey="totalAmount"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-right text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Total
									</SortableTableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{orders.map((order) => {
									const img = primaryProductImage(
										order.items?.[0]?.productImages
									);
									return (
										<TableRow
											key={order.id}
											className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
											onClick={() => navigate(`/orders/${order.id}`)}
										>
											<TableCell>
												<div className="flex items-center gap-3">
													<ProductImage
														image={img}
														className="h-8 w-8 shrink-0 rounded-[var(--r-2)] border border-line-1 bg-surface-fill"
														fallback={
															<Box
																size={14}
																className="text-text-4"
															/>
														}
													/>
													<div className="min-w-0">
														<div className="text-base font-medium text-text-1">
															{order.name}
														</div>
														<div className="font-mono text-xs text-text-4">
															{order.orderNumber}
														</div>
													</div>
												</div>
											</TableCell>
											<TableCell className="text-base text-text-2">
												{order.items?.length ?? 0} item
												{(order.items?.length ?? 0) !== 1 ? 's' : ''}
											</TableCell>
											<TableCell>
												<OrderStatusBadge status={order.status} />
											</TableCell>
											<TableCell className="text-right">
												<CurrencyText
													amount={
														order.summary?.grossAmount ??
														order.summary?.grandTotal ??
														order.totalAmount
													}
													className="font-medium text-text-1"
												/>
											</TableCell>
											<TableCell className="text-base text-text-3">
												{order.createdAt
													? formatDate(order.createdAt)
													: '—'}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<ListFooter list={list} />
		</div>
	);
}
